import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { FilePlus2, CalendarDays } from "lucide-react";
import { api, errMsg, usePoll, fmt } from "../lib/api";
import { PageHeader, Panel, Qty } from "../components/shared";

const PRI = {
  critical: "bg-[#EF4444] text-white", high: "bg-[#F59E0B] text-[#090D16]",
  normal: "bg-[#06B6D4]/20 text-[#06B6D4]", ok: "bg-[#10B981]/20 text-[#10B981]",
};

const Table = ({ head, children, testId }) => (
  <Panel className="overflow-x-auto mb-10" data-testid={testId}>
    <table className="w-full text-sm min-w-[720px]">
      <thead><tr className="border-b border-[#1E293B] text-left font-mono text-[10px] tracking-[0.15em] text-[#6B7280]">
        {head.map((h, i) => <th key={h} className={`px-4 py-3 font-medium ${i ? "text-right" : ""}`}>{h}</th>)}
      </tr></thead>
      <tbody className="divide-y divide-[#1E293B]">{children}</tbody>
    </table>
  </Panel>
);

const H2 = ({ n, children }) => <h2 className="font-display text-lg font-bold mb-3 flex items-center gap-3"><span className="font-mono text-xs text-[#F59E0B]">{n}</span>{children}</h2>;

export default function Forecast() {
  const [horizon, setHorizon] = useState(null);
  const { data } = usePoll(horizon ? `/forecast?horizon_days=${horizon}` : "/forecast", 10000);
  const nav = useNavigate();
  const [creating, setCreating] = useState(null);
  const createPO = async (po) => {
    setCreating(po.supplier_id || "unassigned");
    try {
      const r = await api.post("/purchase-orders", { supplier_id: po.supplier_id, lines: po.lines.map((l) => ({ item_id: l.item_id, qty: l.recommended_order, unit_cost: l.unit_cost || 0 })) });
      toast.success(`${r.data.po_number} created`);
      nav(`/ordering?po=${r.data.id}`);
    } catch (e) { toast.error(errMsg(e)); } finally { setCreating(null); }
  };

  return (
    <div>
      <PageHeader kicker={`// ${data?.window_days ?? 60}-DAY ROLLING ADCR`} title="Forecast & Schedule">
        <button data-testid="schedule-week-btn" onClick={() => nav("/schedule")} className="h-12 px-5 rounded-md bg-[#F59E0B] text-[#090D16] font-bold flex items-center gap-2"><CalendarDays className="w-4 h-4" />Schedule Week</button>
        {[null, 7, 14, 30].map((h) => (
          <button key={h ?? "dos"} data-testid={`forecast-horizon-${h ?? "dos"}`} onClick={() => setHorizon(h)}
            className={`h-12 px-5 rounded-md font-mono text-sm font-bold border ${horizon === h ? "bg-white text-[#090D16]" : "border-[#334155] text-[#9CA3AF]"}`}>{h ? `${h}D` : "POLICY DOS"}</button>
        ))}
      </PageHeader>
      {!data ? <div className="font-mono text-[#6B7280]">CALCULATING…</div> : (<>
        <H2 n="01">Production Schedule Recommendations · Tier 3</H2>
        <Table testId="forecast-schedule-table" head={["PRODUCT", "STOCK / PAR", "ON ORDER", `SHIPPED ${data?.window_days ?? 60}D`, "ADCR /DAY", "DAYS COVER", "DEPLETES", "START BY", "BUILD QTY", "PRIORITY"]}>
          {data.schedule.map((s) => (
            <tr key={s.item_id} data-testid={`schedule-row-${s.sku}`} className="hover:bg-[#172033]">
              <td className="px-4 py-4 font-semibold">{s.name}</td>
              <td className="px-4 text-right font-mono">{fmt(s.stock)} / {fmt(s.par_level)}</td>
              <td className="px-4 text-right font-mono text-[#38BDF8]" data-testid={`schedule-onorder-${s.sku}`}>{s.on_order ? `+${fmt(s.on_order)}` : "—"}</td>
              <td className="px-4 text-right font-mono">{fmt(s.shipped_window)}</td>
              <td className="px-4 text-right font-mono text-[#38BDF8]" data-testid={`adcr-${s.sku}`}>{fmt(s.adcr)}</td>
              <td className="px-4 text-right font-mono">{s.days_of_cover ?? "∞"}</td>
              <td className="px-4 text-right font-mono">{s.depletion_date ?? "—"}</td>
              <td className="px-4 text-right font-mono">{s.recommended_start ?? "—"}</td>
              <td className="px-4 text-right font-mono text-xl font-bold text-[#F59E0B]" data-testid={`build-qty-${s.sku}`}>{s.recommended_qty}</td>
              <td className="px-4 text-right"><span className={`font-mono text-[10px] font-bold px-2 py-1 rounded-sm ${PRI[s.priority]}`}>{s.priority.toUpperCase()}</span></td>
            </tr>
          ))}
        </Table>
        <H2 n="02">Preproduction Build Recommendations · Tier 2</H2>
        <Table testId="forecast-preprod-table" head={["SUB-ASSEMBLY", "NEEDED FOR PLAN", "ON HAND", "ON ORDER", "PAR", "NET SHORT", "BATCH", "BUILD"]}>
          {data.preproduction.map((r) => (
            <tr key={r.item_id} data-testid={`preprod-row-${r.sku}`} className="hover:bg-[#172033]">
              <td className="px-4 py-4 font-semibold">{r.name}</td>
              <td className="px-4 text-right"><Qty v={r.required} unit={r.unit} /></td>
              <td className="px-4 text-right"><Qty v={r.stock} unit={r.unit} /></td>
              <td className="px-4 text-right font-mono text-[#38BDF8]">{r.on_order ? `+${fmt(r.on_order)}` : "—"}</td>
              <td className="px-4 text-right font-mono">{fmt(r.par_level)}</td>
              <td className="px-4 text-right font-mono text-[#9CA3AF]">{fmt(r.net_shortfall)}</td>
              <td className="px-4 text-right font-mono text-[#9CA3AF]">{r.order_unit_qty > 1 ? `×${fmt(r.order_unit_qty)}` : "—"}</td>
              <td className={`px-4 text-right font-mono text-xl font-bold ${r.recommended_build ? "text-[#F59E0B]" : "text-[#10B981]"}`} data-testid={`preprod-build-${r.sku}`}>{r.recommended_build ? fmt(r.recommended_build) : "OK"}</td>
            </tr>
          ))}
          {!data.preproduction.length && <tr><td className="px-4 py-4 text-[#6B7280]" colSpan={8}>No preproduction demand in this horizon.</td></tr>}
        </Table>
        <H2 n="03">Raw Material Restock Recommendations · Tier 1</H2>
        <Table testId="forecast-raw-table" head={["MATERIAL", "NEEDED FOR PLAN", "ON HAND", "ON ORDER", "PAR", "NET SHORT", "PACK", "SUPPLIER", "LEAD", "ORDER (ROUNDED)"]}>
          {data.raw_materials.map((r) => (
            <tr key={r.item_id} data-testid={`raw-row-${r.sku}`} className="hover:bg-[#172033]">
              <td className="px-4 py-4 font-semibold">{r.name}</td>
              <td className="px-4 text-right"><Qty v={r.required} unit={r.unit} /></td>
              <td className="px-4 text-right"><Qty v={r.stock} unit={r.unit} /></td>
              <td className="px-4 text-right font-mono text-[#38BDF8]" data-testid={`raw-onorder-${r.sku}`}>{r.on_order ? `+${fmt(r.on_order)}` : "—"}</td>
              <td className="px-4 text-right font-mono">{fmt(r.par_level)}</td>
              <td className="px-4 text-right font-mono text-[#9CA3AF]">{fmt(r.net_shortfall)}</td>
              <td className="px-4 text-right font-mono text-[#9CA3AF]">{fmt(r.order_unit_qty)}</td>
              <td className="px-4 text-right text-[#9CA3AF]">{r.supplier || "—"}</td>
              <td className="px-4 text-right font-mono">{r.lead_time_days ? `${r.lead_time_days}d` : "—"}</td>
              <td className={`px-4 text-right font-bold ${r.recommended_order ? "text-[#F59E0B]" : "text-[#10B981]"}`} data-testid={`raw-order-${r.sku}`}>{r.recommended_order ? <><Qty v={r.recommended_order} unit={r.unit} className="text-xl" />{r.order_unit_qty > 1 && <div className="font-mono text-[11px] text-[#9CA3AF] font-normal">{r.packs} pack{r.packs > 1 ? "s" : ""} × {fmt(r.order_unit_qty)}</div>}</> : r.covered_by_on_order ? <span className="text-[#38BDF8]" data-testid={`raw-covered-${r.sku}`}>ON ORDER</span> : "OK"}</td>
            </tr>
          ))}
          {!data.raw_materials.length && <tr><td className="px-4 py-4 text-[#6B7280]" colSpan={10}>No raw material demand in this horizon.</td></tr>}
        </Table>
        <H2 n="04">Purchase Order Drafts · by Supplier</H2>
        <div className="grid lg:grid-cols-2 gap-4 mb-10" data-testid="po-drafts">
          {data.po_drafts.map((po) => (
            <Panel key={po.supplier_id || "none"} className="p-5" data-testid={`po-draft-${po.supplier_id || "unassigned"}`}>
              <div className="flex justify-between items-start gap-2 mb-3">
                <div><div className="font-display text-lg font-bold">{po.supplier}</div>{po.lead_time_days && <div className="font-mono text-xs text-[#6B7280]">LEAD {po.lead_time_days}d</div>}</div>
                <div className="text-right"><div className="font-mono text-[10px] text-[#6B7280]">EST. TOTAL</div><div className="font-mono text-xl font-bold text-[#38BDF8]" data-testid={`po-total-${po.supplier_id || "unassigned"}`}>${po.total.toFixed(2)}</div></div>
              </div>
              {po.lines.map((l) => (
                <div key={l.item_id} className="flex justify-between gap-3 py-2 border-t border-[#1E293B] text-sm">
                  <div className="min-w-0"><div className="truncate">{l.name}</div><div className="font-mono text-[11px] text-[#6B7280]">{l.sku} · {l.packs} × {fmt(l.order_unit_qty)} {l.unit}</div></div>
                  <div className="text-right shrink-0"><Qty v={l.recommended_order} unit={l.unit} className="font-bold" /><div className="font-mono text-[11px] text-[#6B7280]">${l.line_total.toFixed(2)}</div></div>
                </div>
              ))}
              <button data-testid={`po-create-btn-${po.supplier_id || "unassigned"}`} disabled={!!creating} onClick={() => createPO(po)}
                className="mt-4 w-full h-12 rounded-md bg-[#F59E0B] text-[#090D16] font-bold flex items-center justify-center gap-2 disabled:opacity-40">
                <FilePlus2 className="w-4 h-4" />{creating === (po.supplier_id || "unassigned") ? "Creating…" : "Create PO"}
              </button>
            </Panel>
          ))}
          {!data.po_drafts.length && <div className="text-[#6B7280] font-mono">NO ORDERS NEEDED</div>}
        </div>
      </>)}
    </div>
  );
}
